import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { PageHeader, Card, StatusPill } from '@/components/Primitives';
import { Modal, Field } from './Extensions';
import { Plus, Trash, Power } from '@phosphor-icons/react';
import { useToast } from '@/hooks/use-toast';

const SCOPES = ['read', 'write', 'webhooks', 'cdr', 'extensions', 'ai'];

const ApiKeys = () => {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const [newKey, setNewKey] = useState(null);
    const { toast } = useToast();

    const load = async () => {
        const r = await api.get('/api-keys');
        setItems(r.data);
    };
    useEffect(() => { load(); }, []);

    const save = async (e) => {
        e.preventDefault();
        try {
            const r = await api.post('/api-keys', editing);
            setNewKey(r.data);
            setEditing(null);
            load();
        } catch (err) {
            toast({ title: 'Chyba', description: err.response?.data?.detail || 'error', variant: 'destructive' });
        }
    };
    const del = async (id) => {
        if (!window.confirm('Smazat API klíč? Aplikace, které ho používají, přestanou fungovat.')) return;
        await api.delete(`/api-keys/${id}`); load();
    };
    const toggle = async (id) => {
        await api.post(`/api-keys/${id}/toggle`); load();
    };

    const blank = {
        name: '',
        scope: { read: true, write: false, webhooks: false, cdr: true, extensions: false, ai: false },
        allowed_ip_cidrs: [],
        rate_limit_per_minute: 60,
    };

    return (
        <>
            <PageHeader
                eyebrow="Developer"
                title="API Keys"
                description="Bearer tokeny pro externí aplikace (CRM, helpdesk, BI). Klíč se zobrazí jen jednou při vytvoření."
                actions={
                    <button onClick={() => setEditing({ ...blank })}
                            data-testid="apikey-create-btn"
                            className="inline-flex items-center gap-2 px-3 py-2 text-xs font-bold uppercase tracking-[0.08em]"
                            style={{ background: 'var(--brand)', color: '#fff', borderRadius: 2 }}>
                        <Plus size={14} weight="bold" />Nový klíč
                    </button>
                }
            />
            <Card>
                {items.length === 0 ? (
                    <div className="py-8 text-center text-sm font-mono" style={{ color: 'var(--text-secondary)' }}>
                        Žádné API klíče.
                    </div>
                ) : (
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-[10px] uppercase tracking-[0.1em]" style={{ color: 'var(--text-secondary)' }}>
                                <th className="text-left py-2">Název</th>
                                <th className="text-left">Prefix</th>
                                <th className="text-left">Scope</th>
                                <th className="text-center">Rate limit</th>
                                <th className="text-center">Stav</th>
                                <th className="text-right">Akce</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((k) => (
                                <tr key={k.id} className="border-t" style={{ borderColor: 'var(--border-default)' }}>
                                    <td className="py-2">{k.name}</td>
                                    <td className="font-mono text-xs">{k.key_prefix}…</td>
                                    <td className="font-mono text-xs">
                                        {SCOPES.filter((s) => k.scope?.[s]).join(', ') || '—'}
                                    </td>
                                    <td className="text-center font-mono">{k.rate_limit_per_minute}/min</td>
                                    <td className="text-center">
                                        <StatusPill status={k.is_active ? 'success' : 'error'} label={k.is_active ? 'aktivní' : 'vypnutý'} />
                                    </td>
                                    <td className="text-right">
                                        <button onClick={() => toggle(k.id)} className="p-1 hover:bg-[var(--bg-surface-hover)]" data-testid={`apikey-toggle-${k.id}`}><Power size={14} /></button>
                                        <button onClick={() => del(k.id)} className="p-1 hover:bg-[var(--bg-surface-hover)] ml-1" data-testid={`apikey-delete-${k.id}`}><Trash size={14} /></button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </Card>

            {editing && (
                <Modal title="Nový API klíč" onClose={() => setEditing(null)}>
                    <form onSubmit={save} className="grid grid-cols-2 gap-3">
                        <Field label="Název" value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} required />
                        <Field label="Rate limit (/min)" value={editing.rate_limit_per_minute} onChange={(v) => setEditing({ ...editing, rate_limit_per_minute: parseInt(v || '60', 10) })} type="number" mono />
                        <div className="col-span-2">
                            <label className="text-[10px] uppercase font-bold" style={{ color: 'var(--text-secondary)' }}>Scope</label>
                            <div className="mt-1 grid grid-cols-3 gap-2">
                                {SCOPES.map((s) => (
                                    <label key={s} className="inline-flex items-center gap-2 text-sm">
                                        <input type="checkbox" checked={!!editing.scope?.[s]}
                                               onChange={(e) => setEditing({ ...editing, scope: { ...editing.scope, [s]: e.target.checked } })} />
                                        {s}
                                    </label>
                                ))}
                            </div>
                        </div>
                        <div className="col-span-2">
                            <label className="text-[10px] uppercase font-bold" style={{ color: 'var(--text-secondary)' }}>Povolené IP / CIDRs (čárka)</label>
                            <input value={(editing.allowed_ip_cidrs || []).join(',')}
                                   onChange={(e) => setEditing({ ...editing, allowed_ip_cidrs: e.target.value.split(',').map((x) => x.trim()).filter(Boolean) })}
                                   placeholder="např. 1.2.3.4/32, 10.0.0.0/24"
                                   className="w-full mt-1 px-2 py-1.5 bg-[var(--bg-app)] border text-sm font-mono"
                                   style={{ borderColor: 'var(--border-default)', borderRadius: 2 }} />
                        </div>
                        <div className="col-span-2 flex justify-end gap-2 mt-3">
                            <button type="button" onClick={() => setEditing(null)} className="px-3 py-2 text-xs font-bold uppercase border" style={{ borderColor: 'var(--border-default)', borderRadius: 2 }}>Zrušit</button>
                            <button type="submit" data-testid="apikey-save-btn" className="px-3 py-2 text-xs font-bold uppercase" style={{ background: 'var(--brand)', color: '#fff', borderRadius: 2 }}>Vytvořit</button>
                        </div>
                    </form>
                </Modal>
            )}

            {newKey && (
                <Modal title="Váš nový API klíč" onClose={() => setNewKey(null)}>
                    <p className="text-sm mb-3">
                        ⚠️ Toto je <strong>jediná příležitost</strong> klíč si zkopírovat — v systému ukládáme jen jeho hash.
                    </p>
                    <code className="block px-3 py-3 font-mono text-xs break-all"
                          style={{ background: '#06080A', borderRadius: 2, color: 'var(--brand)' }}>
                        {newKey.api_key}
                    </code>
                    <div className="mt-3 flex gap-2">
                        <button onClick={() => navigator.clipboard.writeText(newKey.api_key)}
                                className="px-3 py-2 text-xs font-bold uppercase"
                                style={{ background: 'var(--brand)', color: '#fff', borderRadius: 2 }}>
                            Zkopírovat
                        </button>
                        <button onClick={() => setNewKey(null)}
                                className="px-3 py-2 text-xs font-bold uppercase border"
                                style={{ borderColor: 'var(--border-default)', borderRadius: 2 }}>
                            Zavřít
                        </button>
                    </div>
                    <div className="mt-4 text-xs" style={{ color: 'var(--text-secondary)' }}>
                        Použití: <code className="font-mono">Authorization: Bearer {newKey.api_key.slice(0, 12)}…</code>
                    </div>
                </Modal>
            )}
        </>
    );
};

export default ApiKeys;
